import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { showDeleteConfirm } from '../utils/sweetAlert';
import {
  fetchSubIndustries,
  createSubIndustry,
  updateSubIndustry,
  deleteSubIndustry,
  fetchIndustries,
} from '../utils/adminApiService';
import logger from '../utils/logger';
import { useDebounce } from '../hooks/useDebounce';
import AdminSubIndustriesHeader from './AdminSubIndustries/AdminSubIndustriesHeader';
import AdminSubIndustriesStats from './AdminSubIndustries/AdminSubIndustriesStats';
import AdminSubIndustriesToolbar from './AdminSubIndustries/AdminSubIndustriesToolbar';
import AdminSubIndustriesTable from './AdminSubIndustries/AdminSubIndustriesTable';
import AdminSubIndustriesPagination from './AdminSubIndustries/AdminSubIndustriesPagination';
import AdminSubIndustriesAddModal from './AdminSubIndustries/AdminSubIndustriesAddModal';
import AdminSubIndustriesEditModal from './AdminSubIndustries/AdminSubIndustriesEditModal';

const AdminSubIndustries = () => {
  const { user, hasRole, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { industryId, industryName } =
    location.state || { industryId: null, industryName: 'All Industries' };
  
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [filterParentIndustry, setFilterParentIndustry] = useState(industryId || 'all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubIndustry, setEditingSubIndustry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subIndustries, setSubIndustries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [initialLoad, setInitialLoad] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    industry_id: industryId || ''
  });

  // Wait for auth to load before checking access
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access control
  if (!user || !hasRole('admin')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access this page.</p>
          <button 
            onClick={() => navigate('/admin')} 
            className="mt-4 px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-md hover:bg-emerald-700 dark:hover:bg-emerald-600"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Initial load - only on mount or when auth changes
  useEffect(() => {
    if (authLoading) return;
    if (!user || !hasRole('admin')) return;
    
    loadSubIndustries(true);
    loadIndustries();
    setInitialLoad(false);
  }, [authLoading, user]);

  // Reload data when filter changes (but don't show loading spinner after initial load)
  useEffect(() => {
    if (authLoading || initialLoad) return; // Skip if still on initial load
    if (!user || !hasRole('admin')) return;
    
    // Reload data when filter changes, but without showing loading state
    loadSubIndustries(false);
  }, [filterParentIndustry]);

  const loadSubIndustries = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      if (false) { 
        toast.error('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      const data = await fetchSubIndustries(filterParentIndustry !== 'all' ? filterParentIndustry : null);
      
      if (data.success) {
        // Normalize sub-industries data: map backend field names to frontend expected names
        const normalized = (data.sub_industries || []).map(sub => ({
          ...sub,
          name: sub.sub_industry || sub.name || '', // Use sub_industry field from backend
          parent_industry_name: sub.major_industry || sub.parent_industry_name || ''
        }));
        setSubIndustries(normalized);
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      } else {
        logger.error('Error loading sub-industries:', error);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadIndustries = async () => {
    try {
      const data = await fetchIndustries(false);
      if (data.success) {
        setIndustries(data.industries || []);
      }
    } catch (error) {
      logger.error('Error loading industries:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      industry_id: industryId || ''
    });
  };

  const handleAddSubIndustry = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.industry_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const result = await createSubIndustry({
        name: formData.name,
        industry_id: formData.industry_id
      });
      
      if (result.success) {
        setShowAddModal(false);
        resetForm();
        await loadSubIndustries();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (subIndustry) => {
    setEditingSubIndustry(subIndustry);
    setFormData({
      name: subIndustry.name || subIndustry.sub_industry || '',
      industry_id: subIndustry.industry_id || '',
      parent_industry_name: subIndustry.parent_industry_name || subIndustry.major_industry || '',
      parent_industry_region: subIndustry.region || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateSubIndustry = async (e) => {
    e.preventDefault();
    if (!editingSubIndustry) return;

    try {
      setSubmitting(true);
      const result = await updateSubIndustry(editingSubIndustry.id, {
        name: formData.name
      });
      
      if (result.success) {
        setShowEditModal(false);
        setEditingSubIndustry(null);
        resetForm();
        await loadSubIndustries();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubIndustry = async (subIndustryId) => {
    const confirmed = await showDeleteConfirm('this sub-industry');
    if (!confirmed) {
      return;
    }

    try {
      const result = await deleteSubIndustry(subIndustryId);
      
      if (result.success) {
        await loadSubIndustries();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    }
  };

  // Client-side search filtering with debounced search
  const filteredSubIndustries = subIndustries.filter((sub) => {
    const name = sub.name || sub.sub_industry || '';
    const searchTerm = String(debouncedSearchQuery || '').trim();
    if (!searchTerm) return true; // Show all if no search term
    if (!name) return false; // Hide if no name
    try {
      return String(name).toLowerCase().includes(searchTerm.toLowerCase());
    } catch (e) {
      logger.error('Error filtering sub-industries:', e);
      return true; // Show item if filter fails
    }
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredSubIndustries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubIndustries = filteredSubIndustries.slice(startIndex, endIndex);

  // Reset to page 1 when filters change (use debounced search)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, filterParentIndustry]);

  // Remove duplicate industries by ID and add region to display name
  const uniqueIndustries = React.useMemo(() => {
    const seen = new Set();
    return industries
      .filter((ind) => {
        if (seen.has(ind.id)) {
          return false;
        }
        seen.add(ind.id);
        return true;
      })
      .map((ind) => ({
        ...ind,
        displayName: `${ind.name} (${ind.region?.toUpperCase() || 'N/A'})`,
      }));
  }, [industries]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      {/* Toaster removed - using global Toaster from App.js */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminSubIndustriesHeader
          loading={loading}
          total={subIndustries.length}
          industryName={industryName}
          onBackToIndustries={() => navigate('/admin/industry')}
          onBackToAdmin={() => navigate('/admin')}
        />

        <AdminSubIndustriesStats
          loading={loading}
          totalSubIndustries={subIndustries.length}
          filteredCount={filteredSubIndustries.length}
        />

        <div className="bg-white.dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <AdminSubIndustriesToolbar
            searchQuery={searchQuery}
            onSearchChange={(value) => setSearchQuery(value)}
            filterParentIndustry={filterParentIndustry}
            onFilterParentIndustryChange={(value) =>
              setFilterParentIndustry(value)
            }
            uniqueIndustries={uniqueIndustries}
            onAddClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          />

          <AdminSubIndustriesTable
            loading={loading}
            subIndustries={paginatedSubIndustries}
            onEdit={handleEditClick}
            onDelete={handleDeleteSubIndustry}
          />

          <AdminSubIndustriesPagination
            filteredCount={filteredSubIndustries.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={setCurrentPage}
          />
        </div>

        <AdminSubIndustriesAddModal
          visible={showAddModal}
          formData={formData}
          submitting={submitting}
          uniqueIndustries={uniqueIndustries}
          onClose={() => {
            setShowAddModal(false);
            resetForm();
          }}
          onChange={handleFormChange}
          onSubmit={handleAddSubIndustry}
        />

        <AdminSubIndustriesEditModal
          visible={showEditModal}
          editingSubIndustry={editingSubIndustry}
          formData={formData}
          submitting={submitting}
          onClose={() => {
            setShowEditModal(false);
            setEditingSubIndustry(null);
            resetForm();
          }}
          onChange={handleFormChange}
          onSubmit={handleUpdateSubIndustry}
        />
      </div>
    </div>
  );
};

export default AdminSubIndustries;
